import { useEffect, useState } from "react";
import { Mail, Send, CheckCircle2, XCircle, Clock, AlertCircle, Calendar, Play, ReplyAll, Download, ShieldOff, Trash2 } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const STATUS_STYLES = {
  sent:            { bg: "bg-emerald-50",  text: "text-emerald-700", icon: CheckCircle2,  label: "Sent" },
  failed:          { bg: "bg-rose-50",     text: "text-rose-700",    icon: XCircle,       label: "Failed" },
  skipped_no_key:  { bg: "bg-slate-100",   text: "text-slate-600",   icon: Clock,         label: "Skipped (no key)" },
};

const CATEGORY_LABEL = {
  retest_reminder: "Retest reminder",
  results_published: "Results published",
  booking_confirmation: "Booking confirmation",
  booking_confirmation_admin: "Booking confirmation (admin/comp)",
  booking_invoice_customer: "Invoice (customer)",
  booking_pay_link_customer: "Pay-link (customer)",
  booking_pay_link_reminder_customer: "Pay-link reminder",
  customer_report: "Customer report",
  proposal_request_customer: "Estimate request",
  proposal_approved_customer: "Estimate accepted",
  manual_customer_email: "Manual email (admin)",
  admin_booking_notification: "Admin: new paid booking",
  booking_invoice_team: "Team: invoice sent",
  booking_pay_link_team: "Team: pay-link sent",
  booking_pay_link_reminder_team: "Team: pay-link reminder",
  proposal_approved_team: "Team: estimate accepted",
  test: "Test email",
  general: "General",
};

// Phase 2L-A48c — Reply-To audit filter options.
const REPLY_TO_FILTERS = [
  { key: "all",              label: "All",                tone: "bg-slate-100 text-slate-700" },
  { key: "missing",          label: "Missing Reply-To",   tone: "bg-rose-50 text-rose-700" },
  { key: "present",          label: "Has Reply-To",       tone: "bg-emerald-50 text-emerald-800" },
  { key: "customer_facing",  label: "Customer-facing only", tone: "bg-cyan-50 text-cyan-800" },
];

export default function AdminEmails() {
  const [status, setStatus] = useState({ configured: false, from: "" });
  const [metrics, setMetrics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [replyToFilter, setReplyToFilter] = useState("all");
  const [replyToCounts, setReplyToCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter !== "all") q.set("status_filter", filter);
      if (replyToFilter !== "all") q.set("reply_to_filter", replyToFilter);
      const [s, m, l, a] = await Promise.all([
        api.get("/admin/emails/status"),
        api.get("/admin/emails/metrics"),
        api.get(`/admin/emails/logs${q.toString() ? `?${q.toString()}` : ""}`),
        api.get("/admin/emails/reply-to-audit"),
      ]);
      setStatus(s.data);
      setMetrics(m.data);
      setLogs(l.data);
      setReplyToCounts(a.data);
      if (!testTo) setTestTo(s.data.from);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter, replyToFilter]);

  const sendTest = async () => {
    if (!testTo) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/emails/test", { to: testTo });
      if (data.sent) toast.success(`Test email sent to ${testTo}`);
      else if (!data.configured) toast.warning("Resend API key not configured — email skipped (logged)");
      else toast.error("Send failed — check the failed logs");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  // Feb 2026 — Customer-facing email CSV export.
  // Downloads a de-duplicated CSV via the admin-only endpoint.
  // Works identically on preview and production; use it on both
  // environments to get side-by-side email lists.
  const [exportSummary, setExportSummary] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportIncludeTest, setExportIncludeTest] = useState(false);
  const [exportIncludeSuppressed, setExportIncludeSuppressed] = useState(true);

  const loadExportSummary = async () => {
    try {
      const { data } = await api.get(
        `/admin/email-export/summary?include_test_emails=${exportIncludeTest}`
      );
      setExportSummary(data);
    } catch (e) {
      // Non-fatal — the button still works even if summary fails.
      console.warn("email-export summary failed:", e);
    }
  };

  useEffect(() => { loadExportSummary(); }, [exportIncludeTest]);

  const downloadEmailExport = async () => {
    setExportBusy(true);
    try {
      const token = localStorage.getItem("scwt_token") || "";
      const url = `${API_BASE}/admin/email-export.csv?include_test_emails=${exportIncludeTest}&include_suppressed=${exportIncludeSuppressed}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        toast.error(`Export failed (HTTP ${res.status})`);
        return;
      }
      const rows = res.headers.get("X-Aware-Export-Rows") || "?";
      // Prefer the server-supplied filename from Content-Disposition.
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `aware-email-export-${Date.now()}.csv`;
      const blob = await res.blob();
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success(`Downloaded ${rows} rows → ${filename}`);
    } catch (e) {
      toast.error(formatApiError(e?.message) || "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  // Feb 2026 — Email suppression manager (add/list/remove).
  // Every send in email_service.py checks this list BEFORE hitting
  // Resend, so an address on here can never be emailed — including
  // pending retries in any queue.
  const [suppressions, setSuppressions] = useState([]);
  const [suppressLoading, setSuppressLoading] = useState(false);
  const [newSuppressEmail, setNewSuppressEmail] = useState("");
  const [newSuppressReason, setNewSuppressReason] = useState("");
  const [addingSuppress, setAddingSuppress] = useState(false);

  const loadSuppressions = async () => {
    setSuppressLoading(true);
    try {
      const { data } = await api.get("/admin/email-suppressions");
      setSuppressions(data.items || []);
    } catch {
      // silently swallow — non-critical
    } finally {
      setSuppressLoading(false);
    }
  };

  useEffect(() => { loadSuppressions(); }, []);

  const addSuppression = async () => {
    const email = newSuppressEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setAddingSuppress(true);
    try {
      await api.post("/admin/email-suppressions", {
        email,
        reason: newSuppressReason.trim() || "Manually blocked via admin panel",
      });
      toast.success(`${email} added to suppression list — no more outgoing emails`);
      setNewSuppressEmail("");
      setNewSuppressReason("");
      await loadSuppressions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add");
    } finally {
      setAddingSuppress(false);
    }
  };

  const removeSuppression = async (email) => {
    if (!window.confirm(`Unblock ${email}? They will be able to receive emails again.`)) return;
    try {
      await api.delete(`/admin/email-suppressions/${encodeURIComponent(email)}`);
      toast.success(`${email} removed from suppression list`);
      await loadSuppressions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
    }
  };

  const runReminders = async (dry) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/reminders/run${dry ? "?dry_run=true" : ""}`);
      toast.success(`${dry ? "Dry-run" : "Reminders run"}: ${data.sent} sent · ${data.candidates} candidates · ${data.skipped_duplicate} duplicates skipped`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not run reminders");
    } finally {
      setRunning(false);
    }
  };

  const counts = logs.reduce((acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; }, {});

  return (
    <PortalShell title="Email & Reminders" subtitle="Admin">
      {/* Delivery metrics */}
      {metrics && (
        <div data-testid="email-metrics" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1">{metrics.total}</div>
          </div>
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Delivered</div>
            <div className="font-display text-2xl font-bold text-emerald-900 mt-1">{metrics.delivered}</div>
          </div>
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Bounced</div>
            <div className="font-display text-2xl font-bold text-rose-900 mt-1">{metrics.bounced}</div>
          </div>
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Failed</div>
            <div className="font-display text-2xl font-bold text-rose-900 mt-1">{metrics.failed}</div>
          </div>
          <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Open rate</div>
            <div className="font-display text-2xl font-bold text-blue-900 mt-1">{metrics.open_rate}%</div>
          </div>
          <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Click rate</div>
            <div className="font-display text-2xl font-bold text-blue-900 mt-1">{metrics.click_rate}%</div>
          </div>
        </div>
      )}
      {/* Resend status + test send */}
      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <div data-testid="email-status-card" className={`rounded-3xl border p-6 ${status.configured ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-2">
            <Mail className={`w-4 h-4 ${status.configured ? "text-emerald-700" : "text-amber-700"}`} />
            <div className={`text-[10px] uppercase tracking-wider font-bold ${status.configured ? "text-emerald-700" : "text-amber-700"}`}>Resend</div>
          </div>
          <div className={`font-display text-2xl font-bold mt-2 ${status.configured ? "text-emerald-900" : "text-amber-900"}`}>
            {status.configured ? "Connected" : "Not configured"}
          </div>
          <div className="text-xs text-slate-600 mt-2">From: <span className="font-mono">{status.from}</span></div>
          {!status.configured && (
            <p className="text-xs text-amber-800 mt-2 leading-relaxed">
              Add <code className="bg-amber-100 px-1 rounded">RESEND_API_KEY</code> to <code className="bg-amber-100 px-1 rounded">backend/.env</code> and restart the backend. All emails are logged either way.
            </p>
          )}
        </div>

        <div className="rounded-3xl bg-white border border-slate-200 p-6 lg:col-span-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-2">
            <Send className="w-3.5 h-3.5" /> Send a test email
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="email-test-to"
              type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 h-11 rounded-xl"
            />
            <Button data-testid="email-test-send" onClick={sendTest} disabled={sending || !testTo} className="btn-aqua rounded-full font-semibold">
              {sending ? "Sending..." : "Send test"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Logs are written even when the API key is missing (status: <code>skipped_no_key</code>) so you can validate the pipeline before going live.
          </p>
        </div>
      </div>

      {/* Feb 2026 — Customer email list export.
          Aggregates every email across bookings, ebook leads, PWS
          franchisee interest leads, chatbot leads, trade orders,
          proposals, abandoned carts and retest schedules into one
          de-duplicated CSV. Available on preview AND production —
          run it on both environments to get side-by-side lists. */}
      <div className="rounded-3xl bg-white border border-slate-200 p-6 mb-7" data-testid="email-export-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> Customer email list export
            </div>
            <h3 className="font-display text-xl font-bold mt-2 text-slate-900">Download every customer-facing email as CSV</h3>
            <p className="text-sm text-slate-600 mt-1.5 max-w-2xl leading-relaxed">
              Aggregates bookings, ebook leads, PWS franchisee-interest leads,
              chatbot leads, trade orders, proposals, abandoned carts and
              retest-schedule owners into a single de-duplicated CSV. Columns:
              email, name, phone, sources, occurrence count, first/last seen,
              suppressed flag. Run on both preview and production to get
              side-by-side lists.
            </p>

            {exportSummary && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="email-export-summary">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Unique emails</div>
                  <div className="font-display text-2xl font-bold text-slate-900 mt-1">
                    {exportSummary.unique_emails?.toLocaleString?.() ?? exportSummary.unique_emails}
                  </div>
                </div>
                {Object.entries(exportSummary.per_source_rows || {}).slice(0, 3).map(([src, n]) => (
                  <div key={src} className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">{src}</div>
                    <div className="font-display text-2xl font-bold text-slate-900 mt-1">{Number(n).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}

            {exportSummary && Object.keys(exportSummary.per_source_rows || {}).length > 3 && (
              <div className="mt-2 text-xs text-slate-500">
                Other sources:{" "}
                {Object.entries(exportSummary.per_source_rows).slice(3).map(([src, n], i, arr) => (
                  <span key={src}>
                    <span className="font-mono">{src}</span> <span className="text-slate-400">({Number(n).toLocaleString()})</span>{i < arr.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 items-end shrink-0">
            <Button
              data-testid="email-export-download"
              onClick={downloadEmailExport}
              disabled={exportBusy}
              className="btn-aqua rounded-full font-semibold px-6"
            >
              <Download className="w-4 h-4 mr-1.5" />
              {exportBusy ? "Preparing…" : "Download CSV"}
            </Button>

            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="email-export-toggle-test">
              <input
                type="checkbox"
                checked={exportIncludeTest}
                onChange={(e) => setExportIncludeTest(e.target.checked)}
                className="rounded"
              />
              Include test-shaped emails
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="email-export-toggle-suppressed">
              <input
                type="checkbox"
                checked={exportIncludeSuppressed}
                onChange={(e) => setExportIncludeSuppressed(e.target.checked)}
                className="rounded"
              />
              Include suppressed emails
            </label>
          </div>
        </div>
      </div>

      {/* Feb 2026 — Email suppression manager.
          Any address added here is blocked at the send layer in
          email_service.py — every retry / cron / manual send is
          short-circuited with status="suppressed" in email_logs and
          Resend is never called. Use this to permanently stop
          outgoing mail to a specific customer. */}
      <div className="rounded-3xl bg-white border border-slate-200 p-6 mb-7" data-testid="email-suppression-card">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-2">
              <ShieldOff className="w-3.5 h-3.5" /> Email suppression list
            </div>
            <h3 className="font-display text-xl font-bold mt-2 text-slate-900">Permanently block outgoing mail to a specific address</h3>
            <p className="text-sm text-slate-600 mt-1.5 max-w-2xl leading-relaxed">
              Every outgoing email checks this list before Resend is called.
              Addresses added here can never be emailed again (bookings,
              reminders, results, invoices — all blocked) and any pending
              retries stop immediately.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-2 mb-4">
          <Input
            data-testid="suppression-add-email"
            type="email"
            value={newSuppressEmail}
            onChange={(e) => setNewSuppressEmail(e.target.value)}
            placeholder="user@example.com"
            className="h-11 rounded-xl"
          />
          <Input
            data-testid="suppression-add-reason"
            value={newSuppressReason}
            onChange={(e) => setNewSuppressReason(e.target.value)}
            placeholder="Reason (optional) — e.g. Bounced hard, unsubscribed by owner"
            className="h-11 rounded-xl"
          />
          <Button
            data-testid="suppression-add-btn"
            onClick={addSuppression}
            disabled={addingSuppress || !newSuppressEmail.trim()}
            className="rounded-full font-semibold px-5 bg-rose-600 hover:bg-rose-500 text-white"
          >
            <ShieldOff className="w-4 h-4 mr-1.5" />
            {addingSuppress ? "Blocking…" : "Block address"}
          </Button>
        </div>

        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 flex items-center justify-between">
            <span>{suppressions.length} address{suppressions.length === 1 ? "" : "es"} currently blocked</span>
            {suppressLoading && <span className="text-slate-400">Loading…</span>}
          </div>
          {suppressions.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">No addresses currently suppressed on this environment.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="suppression-list">
              {suppressions.map((s) => (
                <li key={s.email} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-slate-900 truncate" data-testid={`suppression-row-${s.email}`}>{s.email}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {s.reason || <em className="text-slate-400">no reason recorded</em>}
                      {s.created_at && <span className="ml-2 text-slate-400">· added {formatDateTimeAU(s.created_at)}</span>}
                      {s.created_by && <span className="ml-1 text-slate-400">by {s.created_by}</span>}
                    </div>
                  </div>
                  <Button
                    data-testid={`suppression-remove-${s.email}`}
                    onClick={() => removeSuppression(s.email)}
                    variant="outline"
                    className="rounded-full text-xs h-8 px-3 border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Unblock
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Reminder cron controls */}
      <div className="rounded-3xl bg-slate-900 text-white p-6 mb-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-400 inline-flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /> Retest reminder scheduler
            </div>
            <h3 className="font-display text-xl font-bold mt-2">Hourly cron · 30d / 7d / due / 14d-past windows</h3>
            <p className="text-sm text-slate-300 mt-1.5 max-w-xl">
              Runs automatically every hour. Dedups via <code className="bg-white/10 px-1 rounded">reminders_sent</code>. Use the buttons to manually trigger or dry-run for testing.
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="reminders-dry-run" onClick={() => runReminders(true)} disabled={running} variant="outline" className="rounded-full font-semibold border-white/20 text-white bg-transparent hover:bg-white/10">
              Dry run
            </Button>
            <Button data-testid="reminders-run" onClick={() => runReminders(false)} disabled={running} className="rounded-full font-semibold bg-cyan-500 hover:bg-cyan-400 text-slate-900">
              <Play className="w-4 h-4 mr-1.5" /> Run now
            </Button>
          </div>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="email-status-filter-bar">
        {["all", "sent", "failed", "skipped_no_key"].map((s) => {
          const meta = s === "all" ? { label: "All", bg: "bg-slate-100", text: "text-slate-700" } : STATUS_STYLES[s];
          const active = filter === s;
          return (
            <button
              key={s}
              data-testid={`email-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                active ? "bg-slate-900 text-white" : `${meta.bg} ${meta.text} hover:opacity-80`
              }`}
            >
              {meta.label}
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/60"}`}>
                {s === "all" ? logs.length : (counts[s] || 0)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Phase 2L-A48c — Reply-To audit filter pills */}
      <div
        className="flex flex-wrap items-center gap-2 mb-4 rounded-2xl bg-slate-50 border border-slate-200 px-3 py-2"
        data-testid="reply-to-filter-bar"
      >
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-slate-500 mr-1">
          <ReplyAll className="w-3.5 h-3.5" /> Reply-To audit
        </div>
        {REPLY_TO_FILTERS.map((f) => {
          const active = replyToFilter === f.key;
          let count = null;
          if (f.key === "missing") count = replyToCounts.customer_facing_missing || 0;
          else if (f.key === "present") count = replyToCounts.customer_facing_present || 0;
          else if (f.key === "customer_facing")
            count = (replyToCounts.customer_facing_present || 0) + (replyToCounts.customer_facing_missing || 0);
          return (
            <button
              key={f.key}
              data-testid={`reply-to-filter-${f.key}`}
              onClick={() => setReplyToFilter(f.key)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                active ? "bg-slate-900 text-white" : `${f.tone} hover:opacity-80`
              }`}
            >
              {f.label}
              {count != null && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/60"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
        {(replyToCounts.customer_facing_missing || 0) > 0 && replyToFilter !== "missing" && (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-700">
            <AlertCircle className="w-3.5 h-3.5" />
            {replyToCounts.customer_facing_missing} customer-facing email{replyToCounts.customer_facing_missing === 1 ? "" : "s"} missing Reply-To
          </span>
        )}
      </div>

      {/* Logs table */}
      <div data-testid="email-logs-table" className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No email logs yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Sent at</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">To</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Subject</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Category</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Ref</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Reply-To</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Provider ID / Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const meta = STATUS_STYLES[l.status] || { bg: "bg-slate-100", text: "text-slate-700", icon: AlertCircle, label: l.status };
                  const Icon = meta.icon;
                  const rt = l.reply_to || l.payload_reply_to || "";
                  const isCustomerFacing = [
                    "booking_confirmation", "booking_confirmation_admin",
                    "booking_invoice_customer", "booking_pay_link_customer",
                    "booking_pay_link_reminder_customer", "customer_report",
                    "proposal_request_customer", "proposal_approved_customer",
                    "manual_customer_email",
                  ].includes(l.category);
                  const missingExpected = isCustomerFacing && !rt;
                  return (
                    <tr key={l.id} data-testid={`email-log-${l.id}`} className={`border-t border-slate-100 hover:bg-slate-50/50 ${missingExpected ? "bg-rose-50/30" : ""}`}>
                      <td className="p-4 text-xs text-slate-500 whitespace-nowrap">{formatDateTimeAU(l.created_at)}</td>
                      <td className="p-4 font-medium text-slate-900">{l.to}</td>
                      <td className="p-4 text-slate-700 max-w-[260px] truncate" title={l.subject}>{l.subject}</td>
                      <td className="p-4 text-xs text-slate-600">{CATEGORY_LABEL[l.category] || l.category}</td>
                      <td className="p-4 text-xs font-mono text-slate-500">{l.related_id || l.booking_ref || "—"}</td>
                      <td className="p-4 text-xs" data-testid={`reply-to-cell-${l.id}`}>
                        {rt ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-mono text-[11px]" title={rt}>
                            <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                            <span className="max-w-[220px] truncate">{rt}</span>
                          </span>
                        ) : missingExpected ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold text-[10px]" data-testid={`reply-to-missing-${l.id}`}>
                            <AlertCircle className="w-3 h-3" /> Missing
                          </span>
                        ) : (
                          <span className="text-slate-400 text-[11px]">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.bg} ${meta.text}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="p-4 text-xs font-mono text-slate-500 max-w-[260px] truncate" title={l.provider_id || l.error || ""}>
                        {l.provider_id || l.error || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
